import React, { useRef } from 'react'
import _ from 'lodash';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechGridNumberControl } from '@agtech/web/components/Grids/Controls/AgTechGridNumberControl';
import { AgTechGridTextControl } from '@agtech/web/components/Grids/Controls/AgTechGridTextControl';
import { SaleLotData, isLotInvoiced, isLotTheSame, useLotGridEntity as useLotGridEntities } from '@sales/data/lots/LotData';
import { useLotDataWriteLotPurchaseAction } from '@sales/data/lots/purchases/LotPurchaseDataActions';
import LotDialog from '../../dialogs/LotDialog';
import { LotLink, LotActionButton } from '../buttons/SaleLotButtons';
import LotStatusBadge from '../badges/LotStatusBadge';
import { SaleContactGridLink } from '../../../contacts/components/buttons/SaleContactButtons';
import { DataActionResults } from '@agtech/core/data/actions/AgTechDataActions';
import { AgTechQueryBuilder } from '@agtech/core/data/queries/AgTechDataQueries';
import { getContactFullName } from '@agtech/data/contacts/ContactData';
import { gridItems } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { useSoldLotDataExportRequest } from '@sales/data/reports/ReportDataActions';
import { useCurrentSale } from '@sales/data/sales/SaleDataStore';
import { AgTechButton, useClickable } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechVerticalPopUpMenu, AgTechVerticalPopUpMenuSection } from '@agtech/web/components/Menus/AgTechVerticalPopUpMenu';
import AgTechHelpModal from '@agtech/web/components/Portals/Modals/AgTechHelpModal';

export type SaleLotSellingGridProps = {
    lotData: SaleLotData
}

const SoldLotExportButton = () => {
    const currentSale = useCurrentSale();
    const soldLotExportRequest = useSoldLotDataExportRequest();
    const chevronRef = useRef(null);
    const chevronClickable = useClickable(chevronRef);

    return (
        <button
            ref={chevronRef}
            className='button button-standard mr-2'
            onClick={e => {
                e.stopPropagation();
                chevronClickable.renderPopup(() => (
                    <AgTechVerticalPopUpMenu>
                        <AgTechVerticalPopUpMenuSection header='Export Sold Lots As'>
                            <AgTechButton
                                text='Excel'
                                icon='fas fa-file-excel'
                                action={async e => await e.actions.executeWithVisual(
                                    async () => { await soldLotExportRequest.execute({ data: { saleId: currentSale.id, format: 'Excel' } }); },
                                    { message: 'Exporting sold lots...', delay: 1000 }
                                )}
                            />
                            <AgTechButton
                                text='CSV'
                                icon='fas fa-file-csv'
                                action={async e => await e.actions.executeWithVisual(
                                    async () => { await soldLotExportRequest.execute({ data: { saleId: currentSale.id, format: 'Csv' } }); },
                                    { message: 'Exporting sold lots...', delay: 1000 }
                                )}
                            />
                        </AgTechVerticalPopUpMenuSection>
                    </AgTechVerticalPopUpMenu>
                ));
            }}
        >
            <i className='fas fa-file-export mr-2' style={{ pointerEvents: 'none' }} />
            Export Sold Lots
            <i className='fas fa-chevron-down text-xs ml-2' style={{ pointerEvents: 'none' }} />
        </button>
    );
}

const SaleLotSellingGrid = (props: SaleLotSellingGridProps) => {
    const writeLotAction = useLotDataWriteLotPurchaseAction();
    const lotGridEntities = useLotGridEntities(props.lotData);

    return (
        <AgTechGrid
            header={{
                title: 'Lots Available To Sell',
                content: () => (
                    <>
                        <SoldLotExportButton />
                        <LotDialog.Button />
                        <AgTechHelpModal.Button
                            title='Selling Lots'
                            content={
                                <div className='row-based gap-y-3 flex'>
                                    <p>Use this page to record lot sales during the auction. Select a lot from the grid, enter the sale price and winning bidder, then confirm the sale.</p>
                                    <p>Sold lots will appear highlighted in the grid and contribute to the <strong>Lots Sold</strong> count. Once a lot is sold, it can be invoiced from the <em>Perform Invoicing</em> tab.</p>
                                </div>
                            }
                        />
                    </>
                )
            }}
            data={{
                itemQuery: gridItems(_.orderBy(lotGridEntities.filter(lt => !isLotInvoiced(lt)), lt => lt.saleOrder))
            }}
            columns={[
                {
                    header: 'Sale Order',
                    value: lot => lot.saleOrder,
                    width: '100px',
                    classes: 'centered bg-slate-100 emphasized'
                },
                {
                    header: 'Lot Number',
                    value: lot => (
                        <LotLink
                            lot={lot}
                            lotCategories={props.lotData.lotCategories}
                            classes='mx-3'
                        />
                    ),
                    width: '120px',
                    classes: 'bg-slate-100 emphasized'
                },
                {
                    header: 'Purchasing Bidder',
                    value: (lot, _, update) => {
                        return (
                            <AgTechGridTextControl
                                value={lot.bidderNumber ?? ''}
                                onUpdated={val => {
                                    update(l => ({
                                        ...l,
                                        bidderNumber: val
                                    }));
                                }}
                            />
                        )
                    },
                    classes: 'snug'
                },
                {
                    header: 'Purchase Price ($)',
                    value: (lot, _, update) => {
                        return (
                            <AgTechGridNumberControl
                                value={lot.purchasePrice ?? NaN}
                                onUpdated={val => {
                                    update(l => ({
                                        ...l,
                                        purchasePrice: val
                                    }));
                                }}
                            />
                        );
                    },
                    classes:'snug'
                },
                {
                    header: 'Contact Info',
                    value: lot => (
                        <div className='stretched relative vertically-centered flex'>
                            {lot.lotContact ? (
                                <SaleContactGridLink
                                    contactName={getContactFullName(lot.lotContact)}
                                    contactId={lot.lotContact.id}
                                />
                            ) : undefined}
                        </div>
                    ),
                    size: 1.5,
                    classes: 'bg-slate-100 emphasized'
                },
                {
                    header: '',
                    value: l => <LotStatusBadge lot={l} classes="border-0 stretched font-semibold !text-base" />,
                    width: '100px'
                }
            ]}
            isRowEntityTheSame={(prev, curr) => {
                let isSame = isLotTheSame(prev, curr) && (prev.bidderNumber === curr.bidderNumber);
                return isSame;
            }}
            actions={{
                rendering: lot => <LotActionButton lot={lot} />
            }}
            editConfig={{
                onEntityUpdated: async props => {
                    const lotResponse = await props.actionExecutor.executeAction(writeLotAction, {
                        originalEntity: props.originalItem,
                        submittedEntity: props.entity.item
                    });

                    return lotResponse.success ? DataActionResults.Success({
                        ...props.entity.item,
                        ...lotResponse.success.data
                    }) : DataActionResults.Failed(lotResponse.errorMessage);
                },
                showStatus: false
            }}
            searchConfig={{
                options: [
                    { field: 'Lot Number', value: lot => lot.lotNumber },
                    { field: 'Description', value: lot => lot.lotDescription },
                    { field: 'Purchasing Bidder', value: lot => lot.bidderNumber ?? '' },
                ]
            }}
            noRecordsConfig={{
                message: "We're sorry, there are no lots available for selling right now.",
                action: {
                    prompt: 'To create a new lot, please click the button below',
                    button: () => <LotDialog.Button classes='button-highlight' />
                }
            }}
            sortingConfig={{
                getSortingKey: lot => lot.saleOrder
            }}
        />
    )
};

export default SaleLotSellingGrid;
